import {useQuery} from "@tanstack/react-query";
import portalPublicService from "@/lib/services/portalPublicService";
import {REGION_ID} from "@/Constants";
import {
  MemberPrimaryAttribute,
  MemberPrimaryAttributeAsKeys,
  OrganizationPublicData,
  PublicData,
} from "@/lib/services/types";

interface OrganizationPublicDataStore extends OrganizationPublicData {
  isLoading: boolean;
  error: Error | null;
  organizationName: string;
  memberPrimaryAttribute?:
  | MemberPrimaryAttribute.EMAIL
  | MemberPrimaryAttribute.MOBILE_NUMBER;
  primaryAttrAsKey:
  | MemberPrimaryAttributeAsKeys.EMAIL
  | MemberPrimaryAttributeAsKeys.MOBILE_NUMBER;
  nonPrimaryAttrAsKey:
  | MemberPrimaryAttributeAsKeys.EMAIL
  | MemberPrimaryAttributeAsKeys.MOBILE_NUMBER;
}

export const useOrganizationData = (): OrganizationPublicDataStore => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["organizationconfigs"],
    queryFn: async (): Promise<PublicData> => {
      return await portalPublicService.getPublicData(REGION_ID);
    },
    staleTime: 5 * 60 * 1000,
    retry: 3,
  });

  const memberPrimaryAttribute =
    data?.organization.configuration?.memberPrimaryAttribute;

  const primaryAttrAsKey = memberPrimaryAttribute
    ? MemberPrimaryAttributeAsKeys[memberPrimaryAttribute]
    : MemberPrimaryAttributeAsKeys.MOBILE_NUMBER; // * If for some reason the "memberPrimaryAttribute" is "undefined", fallback to "mobileNumber".

  const nonPrimaryAttrAsKey =
    memberPrimaryAttribute === MemberPrimaryAttribute.EMAIL
      ? MemberPrimaryAttributeAsKeys.MOBILE_NUMBER
      : MemberPrimaryAttributeAsKeys.EMAIL;

  return {
    organizationName: data?.organization?.organizationName || "",
    memberPrimaryAttribute,
    primaryAttrAsKey,
    nonPrimaryAttrAsKey,
    configuration: {
      ...(data?.organization.configuration || {
        baseCountryCurrencyCode: "",
        memberPrimaryAttribute: MemberPrimaryAttribute.MOBILE_NUMBER,
        portalConfiguration: { allowSelfSignup: false, showRewards: false },
        tierConfiguration: { tierCalculationWindow: 12 },
      }),
      portalConfiguration: {
        ...(data?.organization.configuration?.portalConfiguration || { allowSelfSignup: false, showRewards: false }),
        showRewards: import.meta.env.VITE_SHOW_REWARDS === 'true'
          ? true
          : import.meta.env.VITE_SHOW_REWARDS === 'false'
          ? false
          : (data?.organization.configuration?.portalConfiguration?.showRewards ?? false)
      }
    },
    isLoading,
    error,
  };
};
